import { zodResolver } from '@hookform/resolvers/zod';
import { type InvitationTokenInfo, type RegisterInput, registerSchema } from '@sea-snag/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@sea-snag/ui';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { HTTPError } from 'ky';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAuth, useUser } from '../hooks/use-auth';
import { invitationsApi } from '../lib/api-client';
import { pickInitialOrg } from '../stores/org-store';

const registerSearchSchema = z.object({
  token: z.string().optional(),
});

export const Route = createFileRoute('/register')({
  component: RegisterPage,
  validateSearch: registerSearchSchema,
});

function RegisterPage() {
  const { register: registerUser } = useAuth();
  const { isAuthenticated, user } = useUser();
  const { token } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<InvitationTokenInfo | null>(null);
  const [loadingInvitation, setLoadingInvitation] = useState(!!token);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
  });

  useEffect(() => {
    if (!token) return;
    invitationsApi
      .getByToken(token)
      .then((data) => {
        setInvitation(data);
        setValue('email', data.email);
        setValue('invitationToken', token);
      })
      .catch(() => {
        setError('Invalid or expired invitation link');
      })
      .finally(() => setLoadingInvitation(false));
  }, [token, setValue]);

  if (isAuthenticated && user) {
    // Authenticated user hitting /register?token=... — pass them off to /login
    // which handles the link-existing-account flow. Otherwise navigate to their
    // default org dashboard.
    if (token) {
      return <Navigate to="/login" search={{ inviteToken: token }} />;
    }
    const orgs = (user.organizations ?? []).map((o) => ({
      pid: o.id,
      name: o.name,
      slug: o.slug,
      setupCompletedAt: o.setupCompletedAt,
      role: o.role,
    }));
    const org = pickInitialOrg(orgs);
    if (org) {
      return <Navigate to="/$orgPid/dashboard" params={{ orgPid: org.pid }} />;
    }
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold">Invitation required</CardTitle>
            <CardDescription>
              Sea Snag accounts are created by invitation only. Ask an owner or admin of your
              organization to send you an invite.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/login">Sign in</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loadingInvitation) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Loading invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold">Invitation problem</CardTitle>
            <CardDescription>{error ?? 'Invalid or expired invitation link'}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/login">Sign in</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const onSubmit = async (data: RegisterInput) => {
    setError(null);
    try {
      await registerUser(data);
      // After registration, isAuthenticated becomes true and the guard above will redirect
    } catch (err) {
      if (err instanceof HTTPError) {
        const body = (await err.response.json().catch(() => ({}))) as { message?: string };
        setError(body.message || 'Registration failed');
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  // The invited email is already a registered Sea Snag user — they can't
  // re-register with the same email, so route them to the login flow which
  // will attach the invitation to their existing account.
  if (invitation?.emailExists) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold">Sign in to accept</CardTitle>
            <CardDescription>
              <strong>{invitation.email}</strong> already has a Sea Snag account. Sign in and
              we&apos;ll add this invitation to it automatically.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p>
                Invited to <strong>{invitation.organizationName}</strong> as a{' '}
                <Badge variant="secondary">{invitation.role}</Badge>
              </p>
            </div>
            <Button asChild className="w-full">
              <Link to="/login" search={{ inviteToken: token }}>
                Sign in to continue
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">
            {invitation ? 'Accept Invitation' : 'Create an account'}
          </CardTitle>
          <CardDescription>
            {invitation ? (
              <>
                <strong>{invitation.inviterName}</strong> invited you to join{' '}
                <strong>{invitation.organizationName}</strong> as a{' '}
                <Badge variant="secondary">{invitation.role}</Badge>
              </>
            ) : (
              'Enter your details to create your account'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4"
            aria-label="Registration form"
          >
            {error && (
              <div
                role="alert"
                className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
              >
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                aria-describedby={errors.name ? 'name-error' : undefined}
                {...register('name')}
              />
              {errors.name && (
                <p id="name-error" className="text-sm text-destructive">
                  {errors.name.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                readOnly={!!invitation}
                className={invitation ? 'bg-muted' : ''}
                aria-describedby={errors.email ? 'email-error' : undefined}
                {...register('email')}
              />
              {errors.email && (
                <p id="email-error" className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a password"
                aria-describedby={errors.password ? 'password-error' : 'password-hint'}
                {...register('password')}
              />
              {errors.password && (
                <p id="password-error" className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
              <p id="password-hint" className="text-xs text-muted-foreground">
                Must be at least 8 characters
              </p>
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting
                ? 'Creating account...'
                : invitation
                  ? 'Accept & Create account'
                  : 'Create account'}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-primary underline-offset-4 hover:underline">
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
